// LocationsWidget.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { GoogleMap, LoadScript } from '@react-google-maps/api';
import LocationMarker from './LocationMarker';

const DEFAULT_MAP_CENTER = { lat: 40.7128, lng: -74.0060 };

const MAP_STYLES = [
    { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.park', elementType: 'labels.text', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi.school', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', elementType: 'all', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
];

const STORE_TYPES = [
    { type: '', label: 'All' },
    { type: '03', label: 'Retailers' },
    { type: '23', label: 'Restaurants' },
    { type: '28', label: 'Hotels' },
    { type: '32', label: 'Bars' },
];

const isValidCoord = (n) => typeof n === 'number' && Number.isFinite(n);

function LocationsWidget() {
    const [zipCode, setZipCode] = useState('10001');
    const [pendingZip, setPendingZip] = useState('10001');
    const [locations, setLocations] = useState([]);
    const [storeType, setStoreType] = useState('');
    const [activeMarkerId, setActiveMarkerId] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const fetchLocations = useCallback(async (zip, type) => {
        setIsLoading(true);
        setErrorMessage('');
        try {
            const response = await axios.get('/locations', {
                params: { zip, miles: 10, storeType: type, page: 0, pagesize: 400 },
            });
            setLocations(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            if (error.response && error.response.status === 429) {
                setErrorMessage(error.response.data?.message || 'Too many requests. Please try again later.');
            } else {
                setErrorMessage('Failed to fetch locations.');
            }
            setLocations([]);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchLocations(zipCode, storeType);
    }, [zipCode, storeType, fetchLocations]);

    const handleSubmit = (e) => {
        e.preventDefault();
        setZipCode(pendingZip);
    };

    const firstValidLocation = locations.find(
        (l) => isValidCoord(l.latitude) && isValidCoord(l.longitude)
    );
    const mapCenter = firstValidLocation
        ? { lat: firstValidLocation.latitude, lng: firstValidLocation.longitude }
        : DEFAULT_MAP_CENTER;

    const mapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

    return (
        <div>
            <div className="flex flex-col-reverse md:flex-row min-h-screen">
                <div className="w-full md:w-1/3 p-4 space-y-4">
                    <div>
                        <div className="flex flex-wrap justify-center gap-2 mb-4">
                            {STORE_TYPES.map(({ type, label }) => (
                                <button
                                    key={type}
                                    onClick={() => setStoreType(type)}
                                    className={`px-4 py-1.5 rounded-full text-sm tracking-wide transition-colors ${storeType === type ? 'bg-[#392d23] text-white' : 'border border-[#392d23] text-[#392d23] hover:bg-[#392d23] hover:text-white'}`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                        <form onSubmit={handleSubmit} className="mb-4">
                            {errorMessage && <div className="text-red-500 text-center mb-2">{errorMessage}</div>}
                            <label className="block mb-2 text-[#392d23]">
                                <strong>ZIP Code:</strong>
                                <input
                                    type="text"
                                    value={pendingZip}
                                    onChange={(e) => setPendingZip(e.target.value)}
                                    placeholder="Enter ZIP Code"
                                    className="mt-1 p-2 border border-[#392d23]/30 w-full rounded-md focus:outline-none focus:border-[#392d23] focus:ring-1 focus:ring-[#392d23] text-[#392d23]"
                                />
                            </label>
                            <button type="submit" className="w-full px-4 py-2.5 bg-[#392d23] text-white rounded-md hover:bg-[#5a4738] transition-colors">Search</button>
                        </form>
                        {isLoading ? (
                            <div className="text-center text-[#392d23]/60 p-4">Loading…</div>
                        ) : locations.length > 0 ? (
                            <div className="h-96 overflow-y-auto pr-1">
                                <ul className="space-y-3">
                                    {locations.map((location, index) => (
                                        <li key={index} className="rounded-lg border border-[#392d23]/15 bg-[#f8f5f1] p-4 text-[#392d23] transition-shadow hover:shadow-sm">
                                            <strong className="block mb-1">{location.dba}</strong>
                                            <span className="block text-sm leading-relaxed">{location.address}</span>
                                            <a
                                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location.address)}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="mt-2 inline-block text-sm font-medium underline underline-offset-2 text-[#392d23] hover:text-[#5a4738]"
                                            >
                                                Directions
                                            </a>
                                            <div className="mt-1 text-sm">
                                                {location.phone ? (
                                                    <>Phone: <a href={`tel:+1${location.phone}`} className="text-[#392d23] underline underline-offset-2 hover:text-[#5a4738]">{location.phone}</a></>
                                                ) : (
                                                    <>Phone: not available</>
                                                )}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : (
                            <div className="text-center text-[#392d23]/60 p-4">No locations found.</div>
                        )}
                    </div>
                </div>
                <div className="w-full md:w-2/3 p-4">
                    <LoadScript googleMapsApiKey={mapsApiKey}>
                        <GoogleMap
                            mapContainerStyle={{ height: '100%', minHeight: '80vh', width: '100%', borderRadius: '0.75rem' }}
                            zoom={12}
                            center={mapCenter}
                            options={{ styles: MAP_STYLES }}
                        >
                            {locations.map((location, index) => {
                                if (!isValidCoord(location.latitude) || !isValidCoord(location.longitude)) {
                                    return null;
                                }
                                const id = `${location.dba}-${index}`;
                                return (
                                    <LocationMarker
                                        key={id}
                                        location={location}
                                        isActive={activeMarkerId === id}
                                        onClick={() => setActiveMarkerId(id)}
                                        onClose={() => setActiveMarkerId(null)}
                                    />
                                );
                            })}
                        </GoogleMap>
                    </LoadScript>
                </div>
            </div>
        </div>
    );
}

export default LocationsWidget;
